// The pictures of a ruler, a measuring jug and a kitchen scale. Plain React
// SVG, like practice-graphs.tsx.
//
// None of them prints what it shows: the strip, the water and the needle
// stop against the marks and the child reads them off.

import {
  MEASURE_LABEL_SIZE,
  describeJug,
  describeRuler,
  describeScale,
  jugPicture,
  rulerPicture,
  scalePicture,
  type JugSpec,
  type MeasureSpec,
  type RulerSpec,
  type ScaleSpec,
} from './practice-measure-data';

const INK = '#2d4a3e';
const RULER_FILL = '#f6ecc9';
const STRIP_FILL = '#e07a5f';
const WATER_FILL = '#9cc9e8';
const DIAL_FILL = '#ffffff';
const BODY_FILL = '#eef3ee';

function RulerView({ ruler }: { ruler: RulerSpec }) {
  const picture = rulerPicture(ruler);
  const { body, strip } = picture;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeRuler(ruler)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.2))}px` }}
      >
        <rect
          data-object="true"
          data-from={strip.from}
          data-to={strip.to}
          x={strip.from}
          y={strip.y}
          width={strip.to - strip.from}
          height={strip.height}
          rx="3"
          fill={STRIP_FILL}
          stroke={INK}
          strokeWidth="1.2"
        />
        {[strip.from, strip.to].map((x) => (
          <line
            key={x}
            data-guide="true"
            x1={x}
            y1={strip.y + strip.height}
            x2={x}
            y2={body.y}
            stroke={INK}
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ))}
        <rect
          x={body.x}
          y={body.y}
          width={body.width}
          height={body.height}
          rx="4"
          fill={RULER_FILL}
          stroke={INK}
          strokeWidth="1.5"
        />
        {picture.ticks.map((tick) => (
          <line
            key={tick.value}
            data-tick={tick.label === null ? 'short' : 'long'}
            x1={tick.x}
            y1={body.y}
            x2={tick.x}
            y2={body.y + (tick.label === null ? 9 : 16)}
            stroke={INK}
            strokeWidth={tick.label === null ? '1' : '1.5'}
          />
        ))}
        {picture.ticks.map(
          (tick) =>
            tick.label !== null && (
              <text
                key={tick.value}
                data-number="true"
                x={tick.x}
                y={body.y + 31}
                textAnchor="middle"
                fill={INK}
                fontSize={MEASURE_LABEL_SIZE}
              >
                {tick.label}
              </text>
            ),
        )}
        <text
          x={body.x + body.width - 8}
          y={body.y + body.height - 5}
          textAnchor="end"
          fill={INK}
          fontSize="10"
        >
          cm
        </text>
      </svg>
    </figure>
  );
}

function JugView({ jug }: { jug: JugSpec }) {
  const picture = jugPicture(jug);
  const { left, right, top, bottom } = picture;
  const handleTop = top + 22;
  const handleBottom = Math.min(bottom - 12, handleTop + 90);

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeJug(jug)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.4))}px` }}
      >
        <path
          data-handle="true"
          d={`M ${String(left)} ${String(handleTop)} C ${String(left - 40)} ${String(handleTop)} ${String(left - 40)} ${String(handleBottom)} ${String(left)} ${String(handleBottom)}`}
          fill="none"
          stroke={INK}
          strokeWidth="6"
          strokeLinecap="round"
        />
        <rect
          data-level={jug.level === 0 ? 'empty' : 'water'}
          x={left}
          y={picture.waterTop}
          width={right - left}
          height={bottom - picture.waterTop}
          fill={WATER_FILL}
        />
        <path
          data-jug="true"
          d={`M ${String(left)} ${String(top)} L ${String(left)} ${String(bottom)} L ${String(right)} ${String(bottom)} L ${String(right)} ${String(top)} L ${String(right + 10)} ${String(top - 10)}`}
          fill="none"
          stroke={INK}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {picture.ticks.map((tick) => (
          <line
            key={tick.value}
            data-tick={tick.label === null ? 'short' : 'long'}
            x1={tick.label === null ? right - 9 : picture.tickFrom}
            y1={tick.y}
            x2={right}
            y2={tick.y}
            stroke={INK}
            strokeWidth={tick.label === null ? '1.2' : '1.8'}
          />
        ))}
        {picture.ticks.map(
          (tick) =>
            tick.label !== null && (
              <text
                key={tick.value}
                data-number="true"
                x={picture.labelX}
                y={tick.y}
                dy="0.35em"
                fill={INK}
                fontSize={MEASURE_LABEL_SIZE}
              >
                {tick.label}
              </text>
            ),
        )}
        <text
          data-unit="true"
          x={picture.labelX}
          y={top - 8}
          fill={INK}
          fontSize={MEASURE_LABEL_SIZE}
          fontWeight="700"
        >
          {jug.unit}
        </text>
      </svg>
    </figure>
  );
}

function ScaleView({ scale }: { scale: ScaleSpec }) {
  const picture = scalePicture(scale);
  const { centre, radius, pan, needle } = picture;
  const bodyTop = pan.y + pan.height + 10;

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeScale(scale)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.3))}px` }}
      >
        <rect
          data-pan="true"
          x={pan.x}
          y={pan.y}
          width={pan.width}
          height={pan.height}
          rx="7"
          fill={BODY_FILL}
          stroke={INK}
          strokeWidth="2"
        />
        <rect
          x={centre.x - 8}
          y={pan.y + pan.height}
          width="16"
          height="10"
          fill={INK}
        />
        <rect
          x={centre.x - radius - 14}
          y={bodyTop}
          width={2 * radius + 28}
          height={picture.height - bodyTop - 4}
          rx="18"
          fill={BODY_FILL}
          stroke={INK}
          strokeWidth="2"
        />
        <circle
          cx={centre.x}
          cy={centre.y}
          r={radius}
          fill={DIAL_FILL}
          stroke={INK}
          strokeWidth="2"
        />
        {picture.ticks.map((tick) => (
          <line
            key={tick.value}
            data-tick={tick.label === null ? 'short' : 'long'}
            x1={tick.outer.x}
            y1={tick.outer.y}
            x2={tick.inner.x}
            y2={tick.inner.y}
            stroke={INK}
            strokeWidth={tick.label === null ? '1.2' : '2'}
          />
        ))}
        <line
          data-needle={needle.angle}
          x1={centre.x}
          y1={centre.y}
          x2={needle.tip.x}
          y2={needle.tip.y}
          stroke="#c0392b"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <circle cx={centre.x} cy={centre.y} r="6" fill={INK} />
        {/* The numbers sit on top of the needle with a white edge, so the
            one it passes stays readable. */}
        {picture.ticks.map(
          (tick) =>
            tick.label !== null && (
              <text
                key={tick.value}
                data-number="true"
                x={tick.labelAt.x}
                y={tick.labelAt.y}
                dy="0.35em"
                textAnchor="middle"
                fill={INK}
                fontSize={MEASURE_LABEL_SIZE}
                stroke={DIAL_FILL}
                strokeWidth="4"
                strokeLinejoin="round"
                paintOrder="stroke"
              >
                {tick.label}
              </text>
            ),
        )}
        <text
          data-unit="true"
          x={centre.x}
          y={centre.y + radius - 16}
          textAnchor="middle"
          fill={INK}
          fontSize={MEASURE_LABEL_SIZE}
          fontWeight="700"
        >
          {scale.unit}
        </text>
      </svg>
    </figure>
  );
}

export function MeasurePictureView({ measure }: { measure: MeasureSpec }) {
  switch (measure.type) {
    case 'ruler':
      return <RulerView ruler={measure} />;
    case 'jug':
      return <JugView jug={measure} />;
    case 'scale':
      return <ScaleView scale={measure} />;
  }
}
